import {
  type IGetUserAutocompleteQuery,
  type TGetUserAutocompleteResponse,
  useDebounce,
  useGetUserAutocomplete,
} from "@api";
import { useGlobalContext } from "@root/context";
import { useCallback, useMemo } from "react";
import type { TAutocompleteAuthorsValue } from "./autocomplete";

const groupOptions = (users: TGetUserAutocompleteResponse): Required<TAutocompleteAuthorsValue>[] => {
  const groupedOptions: Required<TAutocompleteAuthorsValue>[] = [];
  for (const user of users) {
    if (user.teams.length === 0) {
      groupedOptions.push({
        _id: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        isDisabled: user.isDisabled,
        teams: [],
      });
    }
    for (let i = 0; i < user.teams.length; ++i) {
      const teams = [...user.teams];
      if (i !== 0) {
        [teams[0], teams[i]] = [teams[i], teams[0]];
      }
      groupedOptions.push({
        _id: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        isDisabled: user.isDisabled,
        teams,
      });
    }
  }
  groupedOptions.sort((a, b) => {
    const teamSort = a.teams[0]?.name.localeCompare(b.teams[0]?.name);

    if (teamSort === 0) {
      return a.firstName.localeCompare(b.firstName);
    }

    return teamSort;
  });
  return groupedOptions;
};

export const useAutocompleteAuthors = (permissions?: string[], query?: IGetUserAutocompleteQuery, teams?: boolean) => {
  const { user } = useGlobalContext();
  const [{ data: users, loading }, getUsers] = useGetUserAutocomplete();

  const parsedPermissions = useMemo(() => permissions ?? ["candidate.dashboard.search"], [permissions]);

  const loadOnOpen = useCallback(() => {
    void getUsers({ ...query, permissions: parsedPermissions });
  }, [getUsers, parsedPermissions, query]);

  const getDebounceUser = useDebounce(
    (_: unknown, value: string) => {
      void getUsers({ ...query, name: value, permissions: parsedPermissions });
    },
    750,
    [getUsers, parsedPermissions, query]
  );

  if (query?.showDisabled && users) {
    users.sort((prev, curr) => Number(prev.isDisabled) - Number(curr.isDisabled));
  }

  const options: TGetUserAutocompleteResponse = useMemo(() => users ?? [], [users]);

  return {
    loading,
    options: (teams ?? user.organisation.features.team.active) ? groupOptions(options) : options,
    loadOnOpen,
    getDebounceUser,
  };
};
